import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HeartPulse, Lock, ShieldCheck, Download, Loader2, KeyRound, FileText } from "lucide-react";

export default function EmployeeHealth() {
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/me/health/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);

  const savePin = async () => {
    if (newPin.length < 4) return toast.error("El PIN debe tener al menos 4 dígitos");
    setBusy(true);
    try {
      await api.post("/me/health/pin", { pin: newPin, current_pin: currentPin || undefined });
      toast.success("PIN de salud guardado");
      setNewPin(""); setCurrentPin("");
      const { data } = await api.get("/me/health/status"); setStatus(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unlock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/health/unlock", { pin });
      setDocs(data.documents);
      toast.success("Documentos de salud desbloqueados");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const dl = async (id, name) => {
    try {
      const res = await api.get(`/documents/${id}/download`, { responseType: "blob", headers: { "X-Health-PIN": pin } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.download = name || "documento"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar"); }
  };

  if (!status) return null;
  if (!status.available) return <Card className="p-8 text-center text-slate-400 border-slate-200 shadow-none">La sección de salud no está disponible en el plan de tu empresa.</Card>;

  return (
    <div className="max-w-xl space-y-5">
      <Card className="p-6 border-violet-200 bg-violet-50/40 shadow-none">
        <div className="flex items-center gap-2 text-violet-700 font-semibold"><Lock className="h-4 w-4" /> Zona privada de salud</div>
        <p className="text-sm text-slate-500 mt-1">Tus documentos médicos están protegidos por un PIN personal. Solo tú puedes verlos.</p>
      </Card>

      {!status.pin_set ? (
        <Card className="p-6 border-slate-200 shadow-none space-y-3">
          <div className="flex items-center gap-2 font-heading font-semibold"><KeyRound className="h-4 w-4 text-slate-400" /> Crea tu PIN de salud</div>
          <Input type="password" inputMode="numeric" placeholder="PIN (4-8 dígitos)" value={newPin} onChange={(e) => setNewPin(e.target.value)} data-testid="health-new-pin" />
          <Button onClick={savePin} disabled={busy} className="brand-btn w-full" data-testid="health-save-pin">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear PIN"}</Button>
        </Card>
      ) : docs === null ? (
        <Card className="p-6 border-slate-200 shadow-none space-y-3">
          <div className="flex items-center gap-2 font-heading font-semibold"><ShieldCheck className="h-4 w-4 text-slate-400" /> Introduce tu PIN</div>
          <Input type="password" inputMode="numeric" placeholder="Tu PIN de salud" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="health-pin" />
          <Button onClick={unlock} disabled={busy} className="brand-btn w-full" data-testid="health-unlock">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desbloquear"}</Button>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-700">Mis documentos de salud</h3>
            <Button size="sm" variant="ghost" onClick={() => { setDocs(null); setPin(""); }} data-testid="health-lock">Bloquear</Button>
          </div>
          {docs.map((d) => (
            <Card key={d.id} className="p-3.5 border-slate-200 shadow-none flex items-center justify-between" data-testid={`health-doc-${d.id}`}>
              <div className="text-sm text-slate-800 flex items-center gap-2"><FileText className="h-4 w-4 text-slate-300" /> {d.title}<span className="text-xs text-slate-400">{d.issue_date || ""}</span></div>
              {d.file_path && <Button size="icon" variant="ghost" onClick={() => dl(d.id, d.file_name)} data-testid={`health-dl-${d.id}`}><Download className="h-4 w-4" /></Button>}
            </Card>
          ))}
          {!docs.length && <div className="text-sm text-slate-400 py-2">Aún no tienes documentos de salud.</div>}
        </div>
      )}

      {status.pin_set && (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-600">Cambiar mi PIN</summary>
          <div className="mt-3 space-y-2 max-w-xs">
            <Input type="password" placeholder="PIN actual" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} data-testid="health-current-pin" />
            <Input type="password" placeholder="Nuevo PIN" value={newPin} onChange={(e) => setNewPin(e.target.value)} data-testid="health-change-pin" />
            <Button size="sm" variant="outline" onClick={savePin} disabled={busy} data-testid="health-change-btn">Actualizar PIN</Button>
          </div>
        </details>
      )}
    </div>
  );
}
